import React from 'react';
import { FileText, Video, Image } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { ActionsMenu } from '../common/ActionsMenu';
import { ErrorHandlingMedia } from '../common/ErrorHandlingMedia';
import { normalizeMedia } from '../../utils/mediaUtils';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { PostStatus } from '../common/PostStatusDropdown';

interface Post {
  id: string;
  content: string;
  format: 'regular' | 'reel' | 'text' | 'video';
  media: {
    type: 'video' | 'image';
    url: string;
    uploadedAt?: string;
    platforms?: string;
    fileSize?: number;
    status?: string;
    fileName?: string;
    mimeType?: string;
    thumbnailUrl?: string;
  } | null;
  platforms: string[];
  status: PostStatus;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
  youtubeVideoId?: string;
  youtubeVideoUrl?: string;
  brandId?: string;
  tiktokPublishId?: string;
  publish_id?: string;
}

interface SocialMediaCardProps {
  post: Post;
  accountImage?: string;
  brandLogo?: string;
  username?: string;
  brandName?: string;
  onDelete?: (postId: string) => void;
}

export const SocialMediaCard: React.FC<SocialMediaCardProps> = ({ 
  post,
  accountImage,
  brandLogo = 'https://ai-coach.socialness.ai/brand/favicon-proxy?domain=socialness.ai',
  brandName = 'Socialness.ai',
  onDelete
}) => {
  const getStatusEmoji = (status: PostStatus) => {
    switch (status) {
      case 'draft':
        return '📝';
      case 'scheduled':
        return '📆';
      case 'waiting_for_approval':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'published':
        return '✅';
      case 'rejected':
        return '❌';
      case 'error':
        return '⚠️';
      case 'ready_to_publish':
        return '🚀';
      default:
        return '';
    }
  };

  const getMediaIcon = (post: Post) => {
    const normalizedMedia = normalizeMedia(post.media);
    if (!normalizedMedia) {
      return <FileText className="w-4 h-4" />;
    }
    return normalizedMedia.type === 'video' ? 
      <Video className="w-4 h-4" /> : 
      <Image className="w-4 h-4" />;
  };

  const getMediaFormatEmoji = (post: Post) => {
    // Determine media format based on post properties
    const normalizedMedia = normalizeMedia(post.media);
    if (!normalizedMedia) {
      return '📝'; // Text only
    }
    
    if (normalizedMedia.type === 'video') {
      return '🎬'; // Video
    }
    
    if (normalizedMedia.type === 'image') {
      // Check if it's a carousel (multiple images) - we'd need to check if there are multiple media items
      // For now, assume single image
      return '🖼️'; // Image
    }
    
    return '📱'; // Default fallback
  };

  const getDate = () => {
    if (post.status === 'published' || post.status === 'waiting_for_approval' || post.status === 'processing') {
      return new Date(post.updatedAt).toLocaleDateString();
    } else if (post.status === 'scheduled' && post.scheduledFor) {
      return new Date(post.scheduledFor).toLocaleDateString();
    }
    return null;
  };

  const date = getDate();

  const handlePostModify = () => {
    // This will be captured by the parent onClick
  };
  
  const handlePostDelete = () => {
    if (onDelete) {
      onDelete(post.id);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex gap-3">
            <div className="flex items-center">
              <img 
                src={accountImage || brandLogo} 
                alt={brandName}
                className="w-14 h-14 rounded-lg object-cover border border-black"
              />
            </div>
            <div>
              <span className="text-base font-semibold block">{brandName}</span>
              {date && (
                <span className="text-xs text-gray-400 block">
                  {date}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {getStatusEmoji(post.status)}
              </span>
              <ActionsMenu 
                onModify={handlePostModify}
                onDelete={handlePostDelete}
                status={post.status}
                className="z-10"
              />
            </div>
            <div className="flex gap-1">
              {post.platforms.map((platform) => (
                <SocialIcon 
                  key={platform}
                  platform={platform as SocialPlatform}
                  size={16}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const normalizedMedia = normalizeMedia(post.media);
        return normalizedMedia && (
          <div className="relative pb-[125%] bg-gray-100 overflow-visible">
            <ErrorHandlingMedia
              src={normalizedMedia.url}
              alt="Post media"
              type={normalizedMedia.type}
              className="absolute inset-0 w-full h-full object-cover"
              controls={normalizedMedia.type === 'video'}
            />
            {/* Media format emoji in bottom right corner */}
            <div className="absolute bottom-0 right-0 bg-white text-gray-800 text-xs px-2 py-1 rounded-full flex items-center gap-1 transform translate-x-1/2 translate-y-1/2 shadow-sm border border-gray-200">
              <span>{getMediaFormatEmoji(post)}</span>
            </div>
          </div>
        );
      })()}

      <div className="p-6">
        <div className="flex justify-between items-start">
          <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1 pr-3">
            {post.content}
          </p>
          {/* Media format emoji for all posts */}
          {!normalizeMedia(post.media) && (
            <div className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1 flex-shrink-0">
              <span>{getMediaFormatEmoji(post)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};